<template>
  <AuthLayout class="login-page">
    <div class="login-content">
      <AppLogo />
      <h2 class="login-title">Welcome Back!</h2>

      <MessageBanner
        v-if="welcomeMessage"
        :message="welcomeMessage"
        variant="success"
      />

      <!-- Local Login Form -->
      <form @submit.prevent="login" class="login-form">
        <InputField
          id="username"
          v-model="loginUsername"
          label="Username"
          icon="fa-user"
          placeholder="Enter your username"
        />

        <div class="password-field">
          <InputField
            id="password"
            v-model="loginPassword"
            :type="showPassword ? 'text' : 'password'"
            label="Password"
            icon="fa-lock"
            placeholder="Enter your password"
            class="password-input"
          />
          <button
            type="button"
            class="toggle-password"
            @click="showPassword = !showPassword"
            :aria-label="showPassword ? 'Hide password' : 'Show password'"
          >
            {{ showPassword ? 'Hide' : 'Show' }}
          </button>
        </div>

        <AlertBox v-if="loginError" :message="loginError" variant="error" />

        <button
          type="submit"
          class="login-button"
          :disabled="!canSubmit || loading"
        >
          <span v-if="loading" class="loader"></span>
          {{ loading ? 'Logging in...' : 'Login' }}
        </button>
      </form>

      <!-- Divider -->
      <div class="divider" role="separator" aria-label="or continue with"></div>

      <!-- Google Login -->
      <button
        type="button"
        class="google-button"
        @click="beginGoogleLogin"
        :aria-label="'Continue with Google'"
      >
        <svg class="google-icon" viewBox="0 0 48 48" aria-hidden="true">
          <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.6 32.6 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.8 3l5.7-5.7C33.6 6.1 29.1 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
          <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.4 16.1 18.8 13 24 13c3 0 5.7 1.1 7.8 3l5.7-5.7C33.6 6.1 29.1 4 24 4 16.1 4 9.3 8.5 6.3 14.7z"/>
          <path fill="#4CAF50" d="M24 44c5.2 0 9.8-1.9 13.2-5.1l-6.1-5c-2 1.5-4.6 2.4-7.1 2.4-5.2 0-9.6-3.5-11.1-8.3l-6.6 5.1C9.3 39.5 16.1 44 24 44z"/>
          <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-1.1 3.2-3.6 5.8-6.9 6.9l6.1 5C37.8 37.9 40 33.3 40 28c0-2.6-.5-5-1.4-7.5z"/>
        </svg>
        <span>Continue with Google</span>
      </button>

      <div class="forgot-link">
        <router-link to="/forgot-password">Forgot your password?</router-link>
      </div>
    </div>
  </AuthLayout>
</template>

<script>
import axios from 'axios';
import { nextTick } from 'vue';
import AuthLayout    from '@/components/AuthLayout.vue';
import AppLogo       from '@/components/AppLogo.vue';
import MessageBanner from '@/components/MessageBanner.vue';
import InputField    from '@/components/InputField.vue';
import AlertBox      from '@/components/Alert.vue';

// Create a dedicated axios instance
const api = axios.create({
  baseURL: process.env.VUE_APP_API_URL || window.location.origin,
  withCredentials: true, // <-- crucial for sending/receiving cookies cross-site
});

// Attach Authorization header from localStorage (if present)
const saved = localStorage.getItem('auth_token');
if (saved) {
  api.defaults.headers.common['Authorization'] = `Bearer ${saved}`;
}


const GOOGLE_CLIENT_ID = process.env.VUE_APP_GOOGLE_CLIENT_ID;



export default {
  name: 'LoginPage',
  components: { AuthLayout, AppLogo, MessageBanner, InputField, AlertBox },
  data() {
    return {
      loginUsername: '', loginPassword: '', showPassword: false,
      loginError: '', welcomeMessage: '', loading: false
    };
  },
  computed: {
    canSubmit() { return this.loginUsername.trim() && this.loginPassword.trim(); }
  },
  methods: {
    async login() {
      if (!this.canSubmit) return;
      this.loading = true; this.loginError = '';
      try {
        const { data } = await api.post('/api/auth/local-login', {
          username: this.loginUsername,
          password: this.loginPassword
        });
        this._onAuthSuccess(data);
      } catch (err) {
        console.error('Login error:', err);
        this.loginError = err.response?.data?.error || 'Login failed. Please try again.';
      } finally { this.loading = false; }
    },

    // --- NEW: Google popup flow using GIS (returns ID token) ----------------
    async beginGoogleLogin() {

        // Guard: don’t even try GIS if the env var isn’t wired up
      if (!GOOGLE_CLIENT_ID) {
        this.loginError = 'Missing Google client ID';
        console.error('VUE_APP_GOOGLE_CLIENT_ID is not defined');
        return;
      }

      try {
        if (!window.google?.accounts?.id) {
          await this._loadGsi(); // lazy-load if script not yet ready
        }


// inside beginGoogleLogin()
if (!GOOGLE_CLIENT_ID) {
  console.error('[FE] Missing GOOGLE_CLIENT_ID');
  this.loginError = 'Missing Google client ID';
  return;
}

    // Initialize GIS - popup UX (debug-friendly)
    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: async ({ credential, select_by }) => {
        if (!credential) {
          console.warn('[FE] No credential from GIS (select_by=', select_by, ')');
          this.loginError = 'Google sign-in was cancelled or failed.';
          return;
        }

        // DEBUG: show what the browser thinks is in the ID token
        try {
          const payload = JSON.parse(atob(credential.split('.')[1] || ''));
          console.log('[FE] cred.len=', credential.length,
                      'aud=', payload.aud,
                      'iss=', payload.iss,
                      'email=', payload.email);
        } catch (e) {
          console.log('[FE] token decode failed:', e.message);
        }

        try {
          // Send the ID token to your backend for verification
          const { data } = await api.post('/api/auth/google/verify', { credential });
          this._onAuthSuccess(data);
        } catch (e) {
          console.error('google verify error', e);
          this.loginError = e.response?.data?.error || 'Google sign-in failed.';
        }
      },
      auto_select: false,
      cancel_on_tap_outside: true,
      context: 'signin'
    });

    // Show the account chooser popup
    window.google.accounts.id.prompt();


        // Show Google account chooser as a popup
        window.google.accounts.id.prompt(); // opens chooser/popup
      } catch (e) {
        console.error('GIS init error', e);
        this.loginError = 'Unable to start Google sign-in.';
      }
    },

    async _loadGsi() {
      await new Promise((resolve, reject) => {
        const existing = document.querySelector('script[src*="accounts.google.com/gsi/client"]');
        if (existing && window.google?.accounts?.id) return resolve();
        const s = document.createElement('script');
        s.src = 'https://accounts.google.com/gsi/client';
        s.async = true; s.defer = true;
        s.onload = resolve; s.onerror = () => reject(new Error('GSI load failed'));
        document.head.appendChild(s);
      });
    },

    _onAuthSuccess(data) {
    // If token exists, we’re in URL-token mode; persist and attach header
    const token = data?.token || null;
    const user  = data?.user || {};

    if (token) {
      localStorage.setItem('auth_token', token);
      // Make sure all future requests send the Authorization header
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      // Cookie mode: the server set httpOnly auth_token; we can’t read it here
      // but withCredentials=true will send it automatically on future requests.
      localStorage.removeItem('auth_token'); // keep storage clean in cookie mode
      delete api.defaults.headers.common['Authorization'];
    }

    localStorage.setItem('user_id',        user.id);
    localStorage.setItem('credit_balance', user.credit_balance);
    localStorage.setItem('role',           user.role);

    this.welcomeMessage = `Welcome ${user.username}, you are logged in as ${user.role}. Redirecting...`;
    nextTick().then(() => {
      const defaultRoute = ['admin','super_admin'].includes(user.role)
        ? { path: '/admin-dashboard/users' }
        : { path: '/ask-ai' };
      this.$router.replace(defaultRoute);
    });
  }

  }
};
</script>


<style scoped>
/* Put the login card in the middle of the viewport */
:deep(.auth-layout.login-page) {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;            /* full screen height */
  padding: 2rem 1rem;           /* breathing room on small screens */
  box-sizing: border-box;
}

/* Constrain the card */
.login-content {
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
}

/* Title */
.login-title {
  font-size: 22px;
  margin: 8px 0;
  font-weight: 600;
  color: var(--color-text, #333);
  text-align: center;
}

/* Vertical rhythm in the form */
.login-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 16px;
}

/* Ensure child inputs stretch full width */
:deep(.input-field),
:deep(.input-field input),
:deep(.input-field .field-wrapper) {
  width: 100%;
}

/* Password field: place toggle button correctly */
.password-field { position: relative; }

/* make room for the toggle button */
.password-field .password-input input {
  padding-right: 3.25rem;   /* space for the Show/Hide button */
}

/* center the Show/Hide button vertically on the input */
.toggle-password {
  position: absolute;
  top: 70%;              /* was 50% */
  right: 0.75rem;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--color-accent, #3b82f6);
  font-size: 0.9rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.toggle-password:hover { text-decoration: underline; }

/* Primary login button */
.login-button {
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  font-weight: 600;
  border: none;
  border-radius: 6px;
  background-color: var(--color-accent, #3b82f6);
  color: #fff;
  cursor: pointer;
  transition: background-color 0.2s ease, opacity 0.2s ease;
}
.login-button:disabled { opacity: 0.6; cursor: not-allowed; }
.login-button:hover:enabled { background-color: #2563eb; }

/* Divider line (kept minimal) */
.divider {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 0.5rem;
  margin: 1rem 0;
}
.divider::before,
.divider::after {
  content: '';
  height: 1px;
  background: #e5e7eb;
}

/* Google button full width, consistent height */
.google-button {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 0.75rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
  color: #111827;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
}
.google-button:hover { background: #f9fafb; }
.google-icon { width: 20px; height: 20px; }

/* Tiny spinner */
.loader {
  border: 2px solid rgba(255,255,255,0.5);
  border-top: 2px solid #fff;
  border-radius: 50%;
  width: 1em; height: 1em;
  margin-right: 0.5em;
  display: inline-block;
  vertical-align: middle;
  animation: spin 1s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* Forgot link alignment */
.forgot-link { text-align: right; margin-top: 0.5rem; }
.forgot-link a {
  font-size: 0.875rem;
  color: var(--color-accent, #3b82f6);
  text-decoration: underline;
}

/* Small-screen polish */
@media (max-width: 400px) {
  .login-content { max-width: 94vw; }
}
</style>
